import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, useApi, useRefresh } from "@/lib/api";
import { PRODUCTS, STAGES, fmtEur } from "@/lib/crm";
import { DocChecklist } from "@/components/DocChecklist";
import { PracticeFiles } from "@/components/PracticeFiles";

const EMPTY = { client_id: "", product: "mutuo", stage: "nuovo", amount: "", duration_months: "", rate: "", bank: "", commission: "", deadline: "", start_date: "", expiry_date: "", fixed_rate_end: "", notes: "", documents: [] };
const num = (v) => (v === "" || v == null ? null : Number(v));

const rata = (amount, months, rate) => {
  const P = Number(amount), n = Number(months), r = Number(rate) / 100 / 12;
  if (!P || !n) return null;
  return r ? (P * r) / (1 - Math.pow(1 + r, -n)) : P / n;
};

const F = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs text-slate-500">{label}</Label>{children}</div>;

export const PracticeFormDialog = ({ open, onOpenChange, practice, clientId }) => {
  const [f, setF] = useState(EMPTY);
  const refresh = useRefresh();
  const { data: clients = [] } = useApi("/clients", { enabled: open && !clientId && !practice });
  const { data: defaults } = useApi("/documents/defaults", { enabled: open, staleTime: Infinity });
  useEffect(() => {
    if (open) setF(practice ? { ...EMPTY, ...Object.fromEntries(Object.entries(practice).map(([k, v]) => [k, v ?? ""])) } : { ...EMPTY, client_id: clientId || "" });
  }, [open, practice, clientId]);
  const docs = Array.isArray(f.documents) && (practice || f.documents.length)
    ? f.documents
    : (defaults?.[f.product] || []).map((name) => ({ name, received: false }));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const est = rata(f.amount, f.duration_months, f.rate);

  const save = async () => {
    if (!f.client_id) return toast.error("Seleziona un cliente");
    const body = { ...f, amount: num(f.amount), duration_months: num(f.duration_months), rate: num(f.rate), commission: num(f.commission), documents: docs };
    try {
      practice ? await api.put(`/practices/${practice.practice_id}`, body) : await api.post("/practices", body);
      toast.success("Pratica salvata");
      refresh();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="practice-form-dialog">
        <DialogHeader><DialogTitle className="font-display">{practice ? "Modifica pratica" : "Nuova pratica"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          {!clientId && !practice && (
            <div className="sm:col-span-2"><F label="Cliente *">
              <Select value={f.client_id} onValueChange={(v) => setF({ ...f, client_id: v })}>
                <SelectTrigger data-testid="practice-client-select"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.client_id} value={c.client_id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </F></div>
          )}
          <F label="Prodotto">
            <Select value={f.product} onValueChange={(v) => setF({ ...f, product: v, documents: practice ? f.documents : [] })}>
              <SelectTrigger data-testid="practice-product-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PRODUCTS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Fase">
            <Select value={f.stage} onValueChange={(v) => setF({ ...f, stage: v })}>
              <SelectTrigger data-testid="practice-stage-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STAGES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label={f.product === "polizza" ? "Premio / Capitale (€)" : "Importo richiesto (€)"}><Input data-testid="practice-amount" type="number" value={f.amount} onChange={set("amount")} /></F>
          <F label="Durata (mesi)"><Input data-testid="practice-duration" type="number" value={f.duration_months} onChange={set("duration_months")} /></F>
          <F label="Tasso TAN (%)"><Input data-testid="practice-rate" type="number" step="0.01" value={f.rate} onChange={set("rate")} /></F>
          <F label="Banca / Istituto / Compagnia"><Input data-testid="practice-bank" value={f.bank} onChange={set("bank")} /></F>
          <F label="Provvigione prevista (€)"><Input data-testid="practice-commission" type="number" value={f.commission} onChange={set("commission")} /></F>
          <F label="Scadenza pratica"><Input data-testid="practice-deadline" type="date" value={f.deadline} onChange={set("deadline")} /></F>
          {f.product === "polizza" && (
            <F label="Scadenza polizza (richiamo 30 gg prima)"><Input data-testid="practice-expiry" type="date" value={f.expiry_date} onChange={set("expiry_date")} /></F>
          )}
          {["cqs", "mutuo", "prestito"].includes(f.product) && (
            <F label="Data erogazione / decorrenza"><Input data-testid="practice-start-date" type="date" value={f.start_date} onChange={set("start_date")} /></F>
          )}
          {f.product === "mutuo" && (
            <F label="Fine tasso fisso / revisione (richiamo 60 gg prima)"><Input data-testid="practice-fixed-end" type="date" value={f.fixed_rate_end} onChange={set("fixed_rate_end")} /></F>
          )}
          {f.product === "cqs" && f.start_date && f.duration_months && (
            <div className="sm:col-span-2 rounded-lg bg-purple-50 border border-purple-100 px-4 py-3 text-sm" data-testid="practice-cqs-renewal">
              Rinnovo possibile (40% rate) dal <b>{(() => { const d = new Date(f.start_date); d.setMonth(d.getMonth() + Math.ceil(Number(f.duration_months) * 0.4)); return d.toLocaleDateString("it-IT"); })()}</b> — ti avviso 2 mesi prima.
            </div>
          )}
          {est && f.product !== "polizza" && (
            <div className="sm:col-span-2 rounded-lg bg-sky-50 border border-sky-100 px-4 py-3 text-sm" data-testid="practice-rata-estimate">
              Rata mensile stimata: <span className="font-mono font-semibold text-sky-700">{fmtEur(est)}</span>
            </div>
          )}
          <div className="sm:col-span-2"><F label="Note"><Textarea data-testid="practice-notes" rows={3} value={f.notes} onChange={set("notes")} /><p data-testid="practice-notes-health-warning" className="text-[11px] text-amber-700">Non inserire nelle note dati sulla salute o altri dati particolari. Le note non vengono inviate all'AI, a meno che tu non lo attivi in Impostazioni.</p></F></div>
          <DocChecklist docs={docs} onChange={(d) => setF({ ...f, documents: d })} />
          {practice && <PracticeFiles practice={practice} docs={docs} onDocReceived={(i) => setF((x) => ({ ...x, documents: docs.map((d, j) => (j === i ? { ...d, received: true } : d)) }))} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button data-testid="practice-save-btn" onClick={save} className="bg-[#0F172A] hover:bg-sky-600">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
